import React, { useEffect, useRef } from 'react';
import { Instagram, Language, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Card,
  CardHeader,
  CardMedia,
  CardContent,
  IconButton,
  Divider,
  Chip,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme
} from '@mui/material';
import trainers from '../data/trainers';
import '../styles/custom.css';

const TrainersPage = () => {
  const theme = useTheme();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    if (sectionRef.current) {
      const sections = sectionRef.current.querySelectorAll('.trainers-section-content');
      sections.forEach(section => observer.observe(section));
    }

    return () => observer.disconnect();
  });

  return (
    <Box className="section-padding" sx={{ backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 600,
                mb: 3,
                maxWidth: '100%',
                textAlign: 'center'
              }}
            >
              Our Expert <Box component="span" sx={{ color: theme.palette.primary.main }}>Trainers</Box><br />
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 600, mx: 'auto' }}>
            Meet the certified professionals who are dedicated to guiding your fitness journey
            </Typography>
          </Box>
        </Box>

        {/* Trainers Section */}
        <Box className="trainers-section-content" ref={sectionRef}>
          <Grid container spacing={4} justifyContent="center">
            {trainers.map((trainer, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card sx={{ maxWidth: 365, display: 'flex', flexDirection: 'column' }}>
                  <CardHeader
                    action={
                      (trainer.website || trainer.instagram) ? (
                        <>
                          {trainer.website && (
                            <IconButton aria-label="Website">
                              <Language />
                            </IconButton>
                          )}
                          {trainer.instagram && (
                            <IconButton aria-label="Instagram">
                              <Instagram />
                            </IconButton>
                          )}
                        </>
                      ) : null
                    }
                    title={trainer.name}
                    subheader={trainer.title}
                    sx={{
                      '& .MuiCardHeader-subheader': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                  />
                  <CardMedia
                    component="img"
                    height="380"
                    image={trainer.image}
                    alt={trainer.name}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center 25%',
                      width: '100%'
                    }}
                  />
                  <Accordion sx={{ m:2, boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`trainer-content-${index}`}
                      id={`trainer-header-${index}`}
                      sx={{
                        minHeight: 'auto !important',
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(178, 35, 53, 0.04)',
                        },
                        '&.Mui-expanded': {
                          minHeight: 'auto !important',
                        }
                      }}
                    >
                      <Typography component="span"  sx={{ color: 'primary.main', fontWeight: 500, }}>Read Bio & Experience</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {trainer.bio}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0, fontStyle: 'italic' }}>
                          {`${new Date().getFullYear() - trainer.startYear}+ Years of Experience`}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {trainer.specializations && trainer.specializations.length > 0 && (
                      <>
                        <Typography gutterBottom variant="body2">
                          Specializations
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: '8px' }}>
                          {trainer.specializations.map((spec, specIndex) => (
                            <Chip
                              key={specIndex}
                              label={spec}
                              size="small"
                              sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                mt: specIndex > 2 ? 1 : 0,
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                    {trainer.degrees && trainer.degrees.length > 0 && (
                      <>
                        <Typography gutterBottom variant="body2">
                          Degrees
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: '8px' }}>
                          {trainer.degrees.map((degree, degreeIndex) => (
                            <Chip
                              key={degreeIndex}
                              label={degree}
                              size="small"
                              sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                    {trainer.certifications && trainer.certifications.length > 0 && (
                      <>
                        <Typography gutterBottom variant="body2">
                          Certifications
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: '8px' }}>
                          {trainer.certifications.map((cert, certIndex) => (
                            <Chip
                              key={certIndex}
                              label={cert}
                              size="small"
                              sx={{
                                backgroundColor: 'secondary.main',
                                color: 'white',
                                mt: certIndex > 2 ? 1 : 0,
                                '&:hover': {
                                  backgroundColor: 'secondary.dark',
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default TrainersPage;
